import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { getAccountDeleteRequests } from '../../actions/accountDeletion';
import { 
    Container, 
    Row, 
    Col, 
    Table, 
    Button, 
    Card, 
    Spinner, 
    Alert, 
    Pagination,
    Badge,
    Form
} from "react-bootstrap";

const Deletion = ({ deletionList, loading, getAccountDeleteRequests }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRequests, setTotalRequests] = useState(0);
    const [showProcessed, setShowProcessed] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        document.title = 'Account Deletion Requests';
        loadRequests(currentPage);
    }, [currentPage, showProcessed]);

    const loadRequests = async (page = 1) => {
        try {
            setIsLoading(true);
            setError('');
            const queryString = `page=${page}&latest=true${showProcessed ? '&processed=true' : ''}`;
            await getAccountDeleteRequests(queryString);
        } catch (err) {
            setError('Failed to load account deletion requests. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleFilterChange = () => {
        setShowProcessed(!showProcessed);
        setCurrentPage(1); // Reset to first page when changing filter
    };

    const getStatusBadge = (processed) => {
        return processed ? (
            <Badge bg="success">
                <i className="fas fa-check me-1"></i>
                Processed
            </Badge>
        ) : (
            <Badge bg="warning" text="dark">
                <i className="fas fa-clock me-1"></i>
                Pending
            </Badge>
        );
    };

    const renderPagination = () => {
        if (!totalPages || totalPages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-4">
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {items}
                    <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </Pagination>
            </div>
        );
    };

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <tr>
                    <td colSpan="5" className="text-center py-4">
                        <Spinner animation="border" variant="primary" className="me-2" />
                        Loading requests...
                    </td>
                </tr>
            );
        }

        if (!deletionList || deletionList.length === 0) {
            return (
                <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                        <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                        No account deletion requests found.
                    </td>
                </tr>
            );
        }

        return deletionList.map((obj, index) => (
            <tr key={obj._id || index} className="align-middle">
                <td className="text-center fw-bold">{(currentPage - 1) * 10 + index + 1}</td>
                <td className="text-center">
                    <span className="fw-semibold">{obj.UserPhoneNumber}</span>
                </td>
                <td className="text-center">
                    <code className="text-muted">{obj.userId}</code>
                </td>
                <td className="text-center">
                    <small>
                        {new Date(obj.requestTime).toLocaleDateString()}<br />
                        <span className="text-muted">
                            {new Date(obj.requestTime).toLocaleTimeString()}
                        </span>
                    </small>
                </td>
                <td className="text-center">
                    {getStatusBadge(obj.requestProcessed)}
                </td>
            </tr>
        ));
    };

    if (loading && !deletionList) {
        return (
            <Container className="py-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading account deletion requests...</p>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <Fragment>
            <Container className="py-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                {/* Header */}
                <div className="mb-4">
                    <Link 
                        to="/" 
                        className="btn btn-outline-primary mb-3"
                    >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Dashboard
                    </Link>
                    <div>
                        <h2 className="text-primary mb-1">
                            <i className="fas fa-user-times me-2"></i>
                            Account Deletion Requests
                        </h2>
                        <p className="text-muted mb-0">
                            Manage and review user account deletion requests
                        </p>
                    </div>
                </div>

                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}

                {/* Main Content Card */}
                <Card className="shadow-sm">
                    <Card.Header className="bg-white">
                        <Row className="align-items-center">
                            <Col md={8}>
                                <h5 className="mb-0">
                                    <i className="fas fa-list me-2"></i>
                                    Deletion Requests 
                                    {totalRequests > 0 && (
                                        <Badge bg="secondary" className="ms-2">
                                            {totalRequests} total
                                        </Badge>
                                    )}
                                </h5>
                            </Col>
                            <Col md={4} className="text-end">
                                <Form.Check 
                                    type="switch"
                                    id="show-processed-switch"
                                    label="Show Processed"
                                    checked={showProcessed}
                                    onChange={handleFilterChange}
                                    className="d-inline-block"
                                />
                            </Col>
                        </Row>
                        {totalPages > 1 && (
                            <Row className="mt-2">
                                <Col>
                                    <small className="text-muted">
                                        <i className="fas fa-info-circle me-1"></i>
                                        Page {currentPage} of {totalPages}
                                    </small>
                                </Col>
                            </Row>
                        )}
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table hover className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="text-center" style={{ width: '80px' }}>
                                            <i className="fas fa-hashtag me-1"></i>
                                            #
                                        </th>
                                        <th className="text-center">
                                            <i className="fas fa-phone me-1"></i>
                                            Phone Number
                                        </th>
                                        <th className="text-center">
                                            <i className="fas fa-user me-1"></i>
                                            User ID
                                        </th>
                                        <th className="text-center">
                                            <i className="fas fa-calendar me-1"></i>
                                            Requested On
                                        </th>
                                        <th className="text-center" style={{ width: '120px' }}>
                                            <i className="fas fa-flag me-1"></i>
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderTableContent()}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                    {totalPages > 1 && (
                        <Card.Footer className="bg-white">
                            {renderPagination()}
                        </Card.Footer>
                    )}
                </Card>

                {/* Statistics Footer */}
                {deletionList && deletionList.length > 0 && (
                    <Row className="mt-4">
                        <Col md={6}>
                            <Card className="text-center h-100">
                                <Card.Body>
                                    <h5 className="text-primary">
                                        <i className="fas fa-clock-o me-2"></i>
                                        Pending Requests
                                    </h5>
                                    <h3 className="text-warning mb-0">
                                        {deletionList.filter(req => !req.requestProcessed).length}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="text-center h-100">
                                <Card.Body>
                                    <h5 className="text-primary">
                                        <i className="fas fa-check-circle me-2"></i>
                                        Processed Requests
                                    </h5>
                                    <h3 className="text-success mb-0">
                                        {deletionList.filter(req => req.requestProcessed).length}
                                    </h3>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>
        </Fragment>
    );
};

Deletion.propTypes = {
    getAccountDeleteRequests: PropTypes.func.isRequired,
    deletionList: PropTypes.array,
    loading: PropTypes.bool
};

const mapStateToProps = state => ({
    deletionList: state.accountDeletion.deletionList,
    loading: state.accountDeletion.loading
});

export default connect(mapStateToProps, { getAccountDeleteRequests })(Deletion);